/**
 * MaintenanceGuard — polls /api/system/status every 30s. If admin has flipped
 * the site into TIME OUT mode, it short-circuits the app render with a
 * branded NBA-Top-Shot-style maintenance screen. Admins always bypass.
 *
 * Scope support:
 *   - scope="global" → blocks everything
 *   - scope="runit|tournaments|drops|marketplace" → only shows the TIME OUT
 *     screen when the user is on that specific route
 *
 * Background: uses vanilla fetch so it works even for logged-out visitors.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dices, Clock, Wrench, Trophy, Package, ShoppingBag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SCOPE_ROUTES = {
  runit: ["/runit", "/runit/"],
  tournaments: ["/runit/tournaments", "/tournaments"],
  drops: ["/drops", "/series"],
  marketplace: ["/marketplace"],
};

const SCOPE_ICON = {
  global: Wrench,
  runit: Dices,
  tournaments: Trophy,
  drops: Package,
  marketplace: ShoppingBag,
};

const fmtEta = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  if (diff < 0) return "any minute now";
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "<1 min";
  if (mins < 60) return `~${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `~${h}h ${m}m`;
};

export default function MaintenanceGuard({ children, isAdmin = false }) {
  const [cfg, setCfg] = useState(null);
  const location = useLocation();
  const intervalRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/system/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setCfg(data?.maintenance || null);
      } catch { /* silent */ }
    };
    poll();
    intervalRef.current = setInterval(poll, 30000);
    return () => { alive = false; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Admins always see the real app — even during maintenance.
  if (!cfg || !cfg.enabled || isAdmin) return children;

  // Scoped maintenance — only intercept if the user is on a matching route.
  if (cfg.scope && cfg.scope !== "global") {
    const routes = SCOPE_ROUTES[cfg.scope] || [];
    const hit = routes.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"));
    if (!hit) return children;
  }

  return <MaintenanceScreen cfg={cfg} />;
}

const MaintenanceScreen = ({ cfg }) => {
  const Icon = SCOPE_ICON[cfg.scope] || Wrench;
  const eta = fmtEta(cfg.eta);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const etaLive = fmtEta(cfg.eta) ?? eta; // refresh on tick
  void tick;

  return (
    <div className="fixed inset-0 z-[9998] bg-[#05050a] text-white overflow-hidden" data-testid="maintenance-screen">
      {/* Animated background mesh — same vibe as a TIME OUT loading screen */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 15% 20%, rgba(251,191,36,0.15), transparent 55%), radial-gradient(circle at 85% 85%, rgba(239,68,68,0.12), transparent 55%), radial-gradient(circle at 50% 60%, rgba(245,158,11,0.1), transparent 60%)" }} />
        <div className="absolute inset-0 mix-blend-overlay opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,.06) 0 1px, transparent 1px 14px)" }} />
      </div>

      <div className="relative z-10 h-full flex items-center justify-center p-6">
        <div className="text-center max-w-lg">
          {/* Whistle / wrench icon spinning */}
          <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 flex items-center justify-center shadow-[0_0_60px_-5px_rgba(251,191,36,0.9)] mb-6" style={{ animation: "maintPulse 2.4s ease-in-out infinite" }}>
            <Icon className="text-black" size={44} />
          </div>

          {/* Big title */}
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300 font-black mb-2">System · Official Pause</div>
          <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-amber-200 to-orange-500 leading-none drop-shadow-[0_0_25px_rgba(251,191,36,0.45)]" data-testid="maintenance-title">
            {cfg.title || "TIME OUT"}
          </h1>

          {/* Scope badge */}
          {cfg.scope && cfg.scope !== "global" && (
            <div className="inline-block mt-4 bg-amber-500/15 border border-amber-400/50 text-amber-200 text-[10px] uppercase tracking-[0.3em] font-black rounded-full px-3 py-1">
              Pause · {cfg.scope}
            </div>
          )}

          {/* Message */}
          <p className="text-gray-300 text-base md:text-lg mt-6 leading-relaxed" data-testid="maintenance-message">
            {cfg.message}
          </p>

          {/* ETA */}
          {etaLive && (
            <div className="mt-8 inline-flex items-center gap-2 bg-black/60 border border-white/10 rounded-full px-4 py-2">
              <Clock size={14} className="text-amber-300 animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-gray-300 font-mono">Back in {etaLive}</span>
            </div>
          )}

          {/* Subtle refresh hint */}
          <div className="mt-10 text-[10px] uppercase tracking-[0.3em] text-gray-600 font-mono">
            We're polling every 30s · page will unlock automatically
          </div>
        </div>
      </div>

      <style>{`
        @keyframes maintPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 60px -5px rgba(251,191,36,0.9); }
          50%      { transform: scale(1.06); box-shadow: 0 0 80px -2px rgba(251,191,36,1); }
        }
      `}</style>
    </div>
  );
};
